import { getActiveOrganizationId } from '../auth/AuthContext'
import { getFreshToken } from '../auth/keycloak'
import { runtimeConfig } from '../runtimeConfig'
import type { ProblemDetails } from './types'

/**
 * An API failure carrying the server's Problem Details.
 *
 * The `violations` array is what lets a form put the server's objection next to the
 * field it concerns, rather than showing a generic banner — which matters here because
 * most refusals in this system are specific and actionable ("that move would create a
 * cycle", "percentages total 95, not 100").
 */
/**
 * Resolves an API path against the configured origin.
 *
 * Empty base in development: Vite proxies `/api` to the local API, so a relative path is
 * same-origin and no CORS is involved. Deployed, the SPA is on Static Web Apps and the
 * API is a Container App on a different host, so the origin has to be supplied — and the
 * API must name this origin in `Cors:AllowedOrigins`.
 *
 * Read per call from the runtime configuration rather than captured at module load. It
 * arrives from `/config.json` at startup, and a module-level constant would freeze the
 * build-time fallback before the file had been fetched — which is exactly the bug that
 * would send a client tenant's SPA at the wrong API.
 */
export function apiUrl(path: string): string {
  const base = runtimeConfig().apiBaseUrl
  return base ? `${base}${path}` : path
}

export class ApiError extends Error {
  readonly status: number
  readonly problem: ProblemDetails

  constructor(status: number, problem: ProblemDetails) {
    super(problem.detail ?? problem.title ?? `Request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
  }

  /** Field-level violations, keyed by camelCase field name. */
  get violations(): Record<string, string> {
    const map: Record<string, string> = {}
    for (const v of this.problem.violations ?? []) {
      map[v.field] = v.message
    }
    return map
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init?.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(init?.headers as Record<string, string> | undefined),
  }

  const token = await getFreshToken(30)
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  /*
    The active organization travels as a header on every request.

    Any transport that bypasses this function — a raw fetch, an SSE stream, a
    WebSocket — must attach it too. The API refuses rather than guessing when a
    multi-organization user sends no header, so a forgotten header surfaces as a clear
    400 instead of silently running against the wrong organization.
  */
  const orgId = getActiveOrganizationId()
  if (orgId) {
    headers['X-Organization-Id'] = orgId
  }

  const response = await fetch(apiUrl(path), { ...init, headers })

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  const payload = text ? JSON.parse(text) : undefined

  if (!response.ok) {
    throw new ApiError(response.status, (payload ?? {}) as ProblemDetails)
  }

  return payload as T
}

/**
 * Downloads a file and hands it to the browser.
 *
 * Not an `<a href download>`: an anchor issues a plain navigation, which carries neither
 * the bearer token nor the active-organization header, so the request 401s — or, worse
 * under a future scoping model, succeeds against the wrong organization. Fetching it
 * through the same path as every other call and handing over a blob keeps one set of
 * rules about who is asking.
 */
async function downloadFile(path: string): Promise<void> {
  const headers: Record<string, string> = {}

  const token = await getFreshToken(30)
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const orgId = getActiveOrganizationId()
  if (orgId) {
    headers['X-Organization-Id'] = orgId
  }

  const response = await fetch(apiUrl(path), { headers })

  if (!response.ok) {
    const text = await response.text()
    throw new ApiError(response.status, (text ? JSON.parse(text) : {}) as ProblemDetails)
  }

  // The server names the file; it knows the run and the period, and the URL does not.
  const disposition = response.headers.get('content-disposition') ?? ''
  const match = /filename="?([^";]+)"?/i.exec(disposition)

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)

  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = match?.[1] ?? 'download'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()

  // Revoked on the next tick: revoking synchronously can race the click in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

export const api = {
  get: <T>(path: string) => request<T>(path),

  download: downloadFile,

  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),

  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),

  del: (path: string) => request<void>(path, { method: 'DELETE' }),

  upload: <T>(path: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<T>(path, { method: 'POST', body: form })
  },
}
